// Several events' summaries side by side: pick events from a name -> year tree,
// read the counts as a row per figure and a column per event, and export the same
// thing as a spreadsheet.
//
// Super Admin only (Events.compareSummaries). The counts come from
// POST /events/summaries in one request — the page can't assemble them from the
// per-event endpoints the single-event summary uses, since that would be four
// requests and an event's whole entry list per column.
//
// What the report contains is eventComparison.js's decision, shared with the
// export so the sheet and the screen are the same report.
import { Fragment, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { API_BASE_URL } from "../config";
import {
  MIXED_EVENTS_NOTE,
  buildSections,
  eventColumnLabel,
  eventDateRange,
  sameEventName,
} from "../eventComparison";
import { exportEventComparison } from "../exportExcel";
import { useFullScreenDialog } from "../useFullScreenDialog";

const EVENTS_API = `${API_BASE_URL}/events`;

// Events with no name at all still have to sit somewhere in the tree.
const UNNAMED = "(unnamed)";

// A year's node label. An event with no start date has no year to be filed under,
// and saying so is better than filing it under a guess.
const yearLabel = (e) => (e.year != null ? String(e.year) : "No date");

export default function EventComparisonDialog({ open, onClose, events }) {
  const fullScreen = useFullScreenDialog();
  const [selected, setSelected] = useState([]); // event ids, the tick state
  const [expanded, setExpanded] = useState([]); // event names whose years are open
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(false);

  // The tree: event name (alphabetical) -> its years (most recent first, which is
  // how the Events page lists them and how a comparison is usually read).
  const groups = useMemo(() => {
    const byName = new Map();
    (events || []).forEach(e => {
      const name = (e.name || "").trim() || UNNAMED;
      if (!byName.has(name)) byName.set(name, []);
      byName.get(name).push(e);
    });
    return [...byName.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, list]) => ({
        name,
        events: [...list].sort((a, b) => (b.start_date || "").localeCompare(a.start_date || "")),
      }));
  }, [events]);

  // The selection is held as sorted ids so the fetch below can depend on its
  // *contents* — a Set or an unsorted array would be a new value on every render
  // or on a re-tick in a different order, and refetch for nothing.
  const selectedKey = selected.join(",");

  useEffect(() => {
    if (!open || !selected.length) { setSummaries([]); return undefined; }
    let active = true;
    setLoading(true);
    axios.post(`${EVENTS_API}/summaries`, { event_ids: selected })
      .then(res => { if (active) setSummaries(res.data); })
      .catch(() => { if (active) setSummaries([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedKey]);

  const toggleIds = (ids, on) => setSelected(prev => {
    const next = new Set(prev);
    ids.forEach(id => (on ? next.add(id) : next.delete(id)));
    return [...next].sort((a, b) => a - b);
  });

  const toggleExpanded = (name) => setExpanded(prev =>
    prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);

  const isSelected = (id) => selected.includes(id);

  // The report itself. Built from the response rather than from `selected`, so the
  // columns always match the figures — the server decides the column order (name,
  // then year) and drops any event deleted since the list was loaded.
  const columns = summaries.map(s => s.event);
  const sections = useMemo(() => buildSections(summaries), [summaries]);
  const mixed = columns.length > 1 && !sameEventName(columns);

  const close = () => { setSummaries([]); setSelected([]); onClose(); };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth={false} fullScreen={fullScreen}>
      <DialogTitle>Compare events</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2 }}>
          {/* The picker: tick a name for every year of it, or open it and tick
              the years wanted. Several names can be part-selected at once. */}
          <Paper variant="outlined" sx={{
            width: { xs: "100%", md: 300 }, flexShrink: 0, p: 1,
            maxHeight: { md: "70vh" }, overflow: "auto",
          }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", px: 1, pb: 1 }}>
              <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                Events {selected.length > 0 && `(${selected.length})`}
              </Typography>
              <Button size="small" disabled={!selected.length} onClick={() => setSelected([])}>
                Clear
              </Button>
            </Stack>
            {groups.map(({ name, events: group }) => {
              const ids = group.map(e => e.id);
              const chosen = ids.filter(isSelected).length;
              const isOpen = expanded.includes(name);
              return (
                <Box key={name}>
                  <Stack direction="row" spacing={0} sx={{ alignItems: "center" }}>
                    <IconButton size="small" onClick={() => toggleExpanded(name)}
                                aria-label={`${isOpen ? "Collapse" : "Expand"} ${name}`}>
                      {isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                    </IconButton>
                    <Checkbox size="small" checked={chosen === ids.length}
                              indeterminate={chosen > 0 && chosen < ids.length}
                              onChange={e => toggleIds(ids, e.target.checked)}
                              inputProps={{ "aria-label": name }} />
                    <Typography variant="body2" sx={{ cursor: "pointer" }}
                                onClick={() => toggleExpanded(name)}>
                      {name}
                    </Typography>
                  </Stack>
                  <Collapse in={isOpen} unmountOnExit>
                    <Box sx={{ pl: 4 }}>
                      {group.map(e => (
                        <Stack key={e.id} direction="row" spacing={0} sx={{ alignItems: "center" }}>
                          <Checkbox size="small" checked={isSelected(e.id)}
                                    onChange={ev => toggleIds([e.id], ev.target.checked)}
                                    inputProps={{ "aria-label": `${name} ${yearLabel(e)}` }} />
                          <Typography variant="body2">{yearLabel(e)}</Typography>
                        </Stack>
                      ))}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
            {groups.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                No events yet.
              </Typography>
            )}
          </Paper>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {loading && <CircularProgress size={28} />}
            {!loading && !sections.length && (
              <Typography variant="body2" color="text.secondary">
                Tick one or more events to compare. Tick an event's name for every
                year of it, or open it and pick the years.
              </Typography>
            )}
            {!loading && sections.length > 0 && (
              <Stack spacing={1}>
                {mixed && (
                  <Typography variant="caption" color="text.secondary">
                    {MIXED_EVENTS_NOTE}
                  </Typography>
                )}
                <TableContainer component={Paper} variant="outlined"
                                sx={{ maxHeight: { md: "70vh" } }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "background.paper" } }}>
                        <TableCell>Count</TableCell>
                        {columns.map(e => (
                          <TableCell key={e.id} align="right" sx={{ whiteSpace: "nowrap" }}>
                            {eventColumnLabel(e)}
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                              {eventDateRange(e)}
                            </Typography>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sections.map((section, s) => (
                        <Fragment key={section.title}>
                          {/* Breathing space between one breakdown and the next.
                              Not before the first, which already sits under the
                              header. The sheet does the same (exportExcel.js). */}
                          {s > 0 && (
                            <TableRow>
                              <TableCell colSpan={columns.length + 1}
                                         sx={{ height: 14, p: 0, border: 0 }} />
                            </TableRow>
                          )}
                          <TableRow>
                            <TableCell colSpan={columns.length + 1}
                                       sx={{ bgcolor: "action.hover", fontWeight: 700 }}>
                              {section.title}
                            </TableCell>
                          </TableRow>
                          {section.rows.map(row => (
                            <TableRow hover key={`${section.title}:${row.level}:${row.label}`}>
                              <TableCell sx={{ pl: row.level ? 4 : 2,
                                               fontWeight: row.emphasis ? 700 : undefined }}>
                                {row.label}
                              </TableCell>
                              {row.values.map((v, i) => (
                                <TableCell key={columns[i].id} align="right"
                                           sx={{ fontWeight: row.emphasis ? 700 : undefined,
                                                 // A zero is a real answer here (the
                                                 // event didn't have that thing), but
                                                 // it shouldn't compete with the
                                                 // figures being compared.
                                                 color: v ? undefined : "text.disabled" }}>
                                  {v ?? "—"}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="caption" color="text.secondary">
                  A count no selected event recorded is left out; one only some of
                  them recorded shows as 0 for the rest.
                </Typography>
              </Stack>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button startIcon={<FileDownloadIcon />} disabled={!sections.length}
                onClick={() => exportEventComparison(sections, columns)}>
          Export XLSX
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={close}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
